import { useRef, useState, useEffect } from 'react';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getColor(value, min, max) {
  if (value === 0 || min === max) return '#1a1d2e';
  const ratio = max !== min ? (value - min) / (max - min) : 0.5;
  if (value > 0) {
    const g = Math.round(100 + ratio * 155);
    return `rgba(34, ${g}, 94, ${0.3 + ratio * 0.7})`;
  } else {
    const r = Math.round(100 + Math.abs(ratio) * 155);
    return `rgba(${r}, 68, 68, ${0.3 + Math.abs(ratio) * 0.7})`;
  }
}

export default function HeatmapChart({ data, title }) {
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [width, setWidth] = useState(600);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  if (!data || data.length === 0) return null;

  // Build grid: day (1-7) × hour (0-23)
  const grid = {};
  let minVal = 0, maxVal = 0;
  for (const d of data) {
    const key = `${d.day}-${d.hour}`;
    grid[key] = d;
    if (d.total_pnl < minVal) minVal = d.total_pnl;
    if (d.total_pnl > maxVal) maxVal = d.total_pnl;
  }

  const cellW = Math.max(12, (width - 50) / 24);
  const cellH = Math.max(16, cellW * 0.8);
  const svgW = 50 + cellW * 24;
  const svgH = 20 + cellH * 7 + 25;

  return (
    <div className="heatmap-chart" ref={containerRef}>
      {title && <h4 className="chart-title">{title}</h4>}
      <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block' }}>
        {/* Hour labels */}
        {[0, 3, 6, 9, 12, 15, 18, 21].map(h => (
          <text key={h} x={50 + h * cellW + cellW / 2} y={14} textAnchor="middle"
            fill="#8b8fa3" fontSize={Math.min(10, cellW * 0.6)}>{h}h</text>
        ))}
        {/* Day labels + cells */}
        {[1, 2, 3, 4, 5, 6, 7].map((day, di) => (
          <g key={day}>
            <text x={45} y={20 + di * cellH + cellH / 2 + 4} textAnchor="end"
              fill="#8b8fa3" fontSize={Math.min(11, cellH * 0.6)}>{DAY_LABELS[day - 1]}</text>
            {Array.from({ length: 24 }, (_, h) => {
              const cell = grid[`${day}-${h}`];
              const val = cell ? cell.total_pnl : 0;
              const count = cell ? cell.trade_count : 0;
              return (
                <rect key={h}
                  x={50 + h * cellW} y={20 + di * cellH}
                  width={cellW - 1} height={cellH - 1}
                  rx={2}
                  fill={count > 0 ? getColor(val, minVal, maxVal) : '#12141e'}
                  stroke="#0f1117" strokeWidth={0.5}
                  onMouseEnter={(e) => setTooltip({
                    x: e.clientX, y: e.clientY,
                    text: `${DAY_LABELS[day - 1]} ${h}:00 — ${count} trades, P&L: ${val.toFixed(2)}`
                  })}
                  onMouseLeave={() => setTooltip(null)}
                  style={{ cursor: count > 0 ? 'pointer' : 'default' }}
                />
              );
            })}
          </g>
        ))}
      </svg>
      {tooltip && (
        <div className="heatmap-tooltip" style={{ left: tooltip.x + 10, top: tooltip.y - 30 }}>
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
